// WallMessage.jsx - Shared component for displaying messages in wall view
import anime from 'animejs';
import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import useFitText from 'use-fit-text';

export const WallMessage = ({ wallData = {} }) => {
  const wallDropShadowFilter = wallData?.wallSettings?.dropShadowFilter ||
    'drop-shadow(0 0 1rem rgba(0,0,0,0.25))';
  const isWallOnly = wallData?.isWallOnly || false;
  const isLandscapeOnly = wallData?.isLandscapeOnly || false;
  const addDebugStyle = wallData?.addDebugStyle || '';
  const messageTypeCurrent =
    wallData?.wallMessage?.postItem?.postTypeForWall || 'none';
  const wallTextColor = 'text-white';
  const transitionDuration = 'duration-250';

  // Hide message when backgroundOnly is true (for QR code displays)
  const backgroundOnly = wallData?.wallMessage?.postItem?.backgroundOnly ?? false;
  const messageShow = backgroundOnly
    ? false
    : (wallData?.wallSettings?.wallMessage?.messageShow ?? true);
  const messageFadeDuration =
    wallData?.wallSettings?.wallMessage?.messageFadeDuration || 1000;
  const messageEffectTranslateYIn =
    wallData?.wallSettings?.wallMessage?.messageEffectTranslateYIn || '0';
  const messageEffectTranslateYOut =
    wallData?.wallSettings?.wallMessage?.messageEffectTranslateYOut || '0';
  const messageEffectTranslateXIn =
    wallData?.wallSettings?.wallMessage?.messageEffectTranslateXIn || '0';
  const messageEffectTranslateXOut =
    wallData?.wallSettings?.wallMessage?.messageEffectTranslateXOut || '0';
  const messageEffectScaleIn =
    wallData?.wallSettings?.wallMessage?.messageEffectScaleIn || '1.1';
  const messageEffectScaleOut =
    wallData?.wallSettings?.wallMessage?.messageEffectScaleOut || '0.9';
  const messageFilterBlur =
    wallData?.wallSettings?.wallMessage?.messageFilterBlur || '0.25px';
  const messageEffectBlur =
    wallData?.wallSettings?.wallMessage?.messageEffectBlur || '100px';
  const messageEffectEaseIn =
    wallData?.wallSettings?.wallMessage?.messageEffectEaseIn || 'easeOutSine';
  const messageEffectEaseOut =
    wallData?.wallSettings?.wallMessage?.messageEffectEaseOut || 'easeInSine';

  const [fitTextFinished1, setFitTextFinished1] = useState(false);
  const [fitTextFinished2, setFitTextFinished2] = useState(false);

  const { fontSize: fontSizeMessage1, ref: refMessage1 } = useFitText({
    minFontSize: 20,
    maxFontSize: 1500,
    resolution: 10,
    onFinish: () => {
      setFitTextFinished1(true);
    },
  });

  const { fontSize: fontSizeMessage2, ref: refMessage2 } = useFitText({
    minFontSize: 20,
    maxFontSize: 1500,
    resolution: 10,
    onFinish: () => {
      setFitTextFinished2(true);
    },
  });

  const [message1, setMessage1] = useState(null);
  const [message2, setMessage2] = useState(null);
  const animeScope = useRef(null);
  const [isMessage1Visible, setIsMessage1Visible] = useState(false);

  useEffect(() => {
    // Use postTypeForWall: snap = no message, post/vote = show message
    // Also hide message when backgroundOnly is true (for QR code displays)
    const effectiveMessage = (messageTypeCurrent === 'snap' || backgroundOnly)
      ? null
      : wallData?.wallMessage?.postItem?.message;

    if (effectiveMessage === (isMessage1Visible ? message1 : message2))
      return;
    if (isMessage1Visible)
      setMessage2(effectiveMessage);
    else setMessage1(effectiveMessage);

    setFitTextFinished1(false);
    setFitTextFinished2(false);
    setIsMessage1Visible((oldValue) => !oldValue);
  }, [wallData?.wallMessage?.postItem?.message, messageTypeCurrent, backgroundOnly]);

  useLayoutEffect(() => {
    const inMessage = isMessage1Visible
      ? refMessage1.current
      : refMessage2.current;
    const outMessage = isMessage1Visible
      ? refMessage2.current
      : refMessage1.current;
    const currentMessage = isMessage1Visible ? message1 : message2;
    const previousMessage = isMessage1Visible ? message2 : message1;

    if (animeScope.current) {
      animeScope.current.pause();
    }

    // Case 1: Transitioning TO null (e.g., to snap) - fade out the old message
    if (currentMessage === null && previousMessage !== null && outMessage) {
      animeScope.current = anime({
        targets: outMessage,
        opacity: 0,
        translateY: ['0', messageEffectTranslateYOut],
        translateX: ['0', messageEffectTranslateXOut],
        scale: [1, messageEffectScaleOut],
        filter: ['blur(0px)', 'blur(' + messageEffectBlur + ')'],
        easing: messageEffectEaseOut,
        duration: messageFadeDuration,
        complete: () => {
          if (outMessage) outMessage.style.opacity = '0';
        },
      });
      return;
    }

    // Case 2: Transitioning FROM null (e.g., from snap) - fade in the new message
    if (currentMessage !== null && previousMessage === null && inMessage) {
      // Wait for fitText to finish
      if (
        (isMessage1Visible && !fitTextFinished1) ||
        (!isMessage1Visible && !fitTextFinished2)
      )
        return;

      inMessage.style.opacity = '0';
      animeScope.current = anime({
        targets: inMessage,
        opacity: 1,
        translateY: [messageEffectTranslateYIn, 0],
        translateX: [messageEffectTranslateXIn, 0],
        scale: [messageEffectScaleIn, 1],
        filter: ['blur(' + messageEffectBlur + ')', 'blur(0px)'],
        easing: messageEffectEaseIn,
        duration: messageFadeDuration,
        complete: () => {
          if (inMessage) inMessage.style.opacity = '1';
        },
      });
      return;
    }

    // Case 3: Normal crossfade between two messages
    if (!message1 || !message2) return;

    // Wait for fitText to finish measuring the incoming message
    if (
      (isMessage1Visible && !fitTextFinished1) ||
      (!isMessage1Visible && !fitTextFinished2)
    )
      return;

    if (!inMessage) return;

    inMessage.style.opacity = '0';
    animeScope.current = anime({
      targets: inMessage,
      opacity: 1,
      translateY: [messageEffectTranslateYIn, 0],
      translateX: [messageEffectTranslateXIn, 0],
      scale: [messageEffectScaleIn, 1],
      filter: ['blur(' + messageEffectBlur + ')', 'blur(0px)'],
      easing: messageEffectEaseIn,
      duration: messageFadeDuration,
      begin: () => {
        if (outMessage) {
          anime({
            targets: outMessage,
            opacity: 0,
            translateY: ['0', messageEffectTranslateYOut],
            translateX: ['0', messageEffectTranslateXOut],
            scale: [1, messageEffectScaleOut],
            filter: ['blur(0px)', 'blur(' + messageEffectBlur + ')'],
            easing: messageEffectEaseOut,
            duration: messageFadeDuration,
            complete: () => {
              if (outMessage) outMessage.style.opacity = '0';
            },
          });
        }
      },
      complete: () => {
        if (inMessage) inMessage.style.opacity = '1';
      },
    });
  }, [isMessage1Visible, fitTextFinished1, fitTextFinished2, message1, message2]);

  // z-30 (not z-20) — WallBG's IMG uses will-change-transform which promotes
  // it to a composited layer. Chrome paints composited layers above
  // non-composited elements at the same z-index, so z-20 overlays would
  // disappear BEHIND the background image. z-30 clears the threshold.
  const wallMessageStyle = `z-30 absolute transition-all ${messageShow ? "opacity-100" : "opacity-0"} ` + transitionDuration;
  const wallMessageLayoutStyle =
    messageTypeCurrent == 'post' ||
    messageTypeCurrent == 'snap' ||
    messageTypeCurrent == 'feel' ||
    // 'none' = a message without a postTypeForWall (e.g. the system default
    // message). Without a layout box useFitText scales toward maxFontSize
    // (1500px) → oversized; give it the standard post box.
    messageTypeCurrent == 'none'
      ? isWallOnly
        ? 'top-4/16 h-7/16 left-1/2 w-14/16 -translate-x-1/2'
        : isLandscapeOnly
          ? 'top-5/16 h-7/16 left-1/32 w-1/2 -translate-x-0'
          : 'top-11/32 h-12/32 left-1/2 w-12/16 -translate-x-1/2'
      : messageTypeCurrent == 'vote'
        ? isWallOnly
          ? 'top-4/16 h-7/16 left-1/32 w-1/2 translate-x-0'
          : isLandscapeOnly
            ? 'top-5/16 h-7/16 left-1/32 w-1/2 -translate-x-0'
            : 'top-11/32 h-12/32 left-layout w-1/2 -translate-x-0'
        : '';
  // iOS < 14 Safari renders the element BLACK when an SVG url(#filter) is chained
  // with CSS blur()/drop-shadow() (WebKit bug, fixed in iOS 14). Drop the
  // threshold there — text stays readable via blur + drop-shadow. iOS 14+ and
  // every other browser keep the full "gooey" threshold effect.
  const _ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const _iosMajor = /iphone|ipad|ipod/i.test(_ua)
    ? parseInt((_ua.match(/OS (\d+)_/) || [])[1] || '99', 10)
    : 99;
  const wallMessageFilterStyle =
    (_iosMajor < 14 ? '' : 'url(#threshold) ') +
    'blur(' + messageFilterBlur + ') ' + wallDropShadowFilter;
  const wallMessageSubStyle =
    'absolute size-full flex items-center font-black leading-[0.95em] uppercase';
  const wallMessageSubLayoutStyle =
    messageTypeCurrent == 'post' ||
    messageTypeCurrent == 'snap' ||
    messageTypeCurrent == 'feel' ||
    messageTypeCurrent == 'none'
      ? isWallOnly
        ? 'justify-center text-center'
        : isLandscapeOnly
          ? 'justify-left text-left'
          : 'justify-center text-center'
      : messageTypeCurrent == 'vote'
        ? isWallOnly
          ? 'justify-left text-left'
          : isLandscapeOnly
            ? 'justify-left text-left'
            : 'justify-left text-left'
        : '';

  return (
    <div
      id="wallMessage"
      className={[
        wallMessageStyle,
        wallMessageLayoutStyle,
        wallTextColor,
        addDebugStyle,
      ].join(' ')}
      style={{ filter: wallMessageFilterStyle }}
    >
      <div
        id="wallMessage1"
        ref={refMessage1}
        className={[
          wallMessageSubStyle,
          wallMessageSubLayoutStyle,
          addDebugStyle,
        ].join(' ')}
        style={{ fontSize: fontSizeMessage1, willChange: 'opacity, transform' }}
      >
        {message1}
      </div>

      <div
        id="wallMessage2"
        ref={refMessage2}
        className={[
          wallMessageSubStyle,
          wallMessageSubLayoutStyle,
          addDebugStyle,
        ].join(' ')}
        style={{ fontSize: fontSizeMessage2, willChange: 'opacity, transform' }}
      >
        {message2}
      </div>

      {messageFilterBlur !== '0' && (
        <svg id="filters" className="absolute">
          <defs>
            <filter id="threshold">
              <feColorMatrix
                in="SourceGraphic"
                type="matrix"
                values="1 0 0 0 0
                        0 1 0 0 0
                        0 0 1 0 0
                        0 0 0 255 -140"
              />
            </filter>
          </defs>
        </svg>
      )}
    </div>
  );
};

const areWallMessagePropsEqual = (prevProps, nextProps) => {
  const relevantPrevProps = {
    message: prevProps.wallData?.wallMessage?.postItem?.message,
    postTypeForWall: prevProps.wallData?.wallMessage?.postItem?.postTypeForWall,
    backgroundOnly: prevProps.wallData?.wallMessage?.postItem?.backgroundOnly,
    dropShadow: prevProps.wallData?.dropShadow,
    isLandscapeOnly: prevProps.wallData?.isLandscapeOnly,
    // #248: re-render live on any client-settings change.
    wallSettings: prevProps.wallData?.wallSettings,
  };

  const relevantNextProps = {
    message: nextProps.wallData?.wallMessage?.postItem?.message,
    postTypeForWall: nextProps.wallData?.wallMessage?.postItem?.postTypeForWall,
    backgroundOnly: nextProps.wallData?.wallMessage?.postItem?.backgroundOnly,
    dropShadow: nextProps.wallData?.dropShadow,
    isLandscapeOnly: nextProps.wallData?.isLandscapeOnly,
    wallSettings: nextProps.wallData?.wallSettings,
  };

  return (
    JSON.stringify(relevantPrevProps) === JSON.stringify(relevantNextProps)
  );
};

export const WallMessageMemo = React.memo(
  WallMessage,
  areWallMessagePropsEqual
);
